import React from 'react'
import styled from 'styled-components'
import { Box, Flex, Text } from 'uikit'
import { useTranslation } from 'react-i18next'
import { getRoundResult, Result } from 'state/predictions/helpers'
import { REWARD_RATE } from 'state/predictions/config'
import { multiplyPriceByAmount } from 'utils/prices'
import { useGetCurrentEpoch } from 'state/predictions/hooks'
import { Bet, BetPosition } from 'state/types'
import { useConfig } from 'pages/Predictions/context/ConfigProvider'
import { usePriceMjtUSDT } from 'state/hooks'

import { formatMjt, getMultiplier, getNetPayout } from '../helpers'
import PnlChart from './PnlChart'
import SummaryRow from './SummaryRow'
import EmptyImg from '../../EmptyImg'

interface PnlTabProps {
  hasBetHistory: boolean
  bets: Bet[]
}

interface PnlCategory {
  rounds: number
  amount: number
}

interface PnlSummary {
  won: PnlCategory & { payout: number; bestRound: { id: string; payout: number; multiplier: number } }
  lost: PnlCategory
  entered: PnlCategory
}

const BorderContent = styled.div<{ margin?: string }>`
  border: 1px solid rgba(115, 126, 141, 0.16);
  border-radius: 16px;
  padding: 16px 20px;
  margin: ${({ margin }) => margin ?? '0'};
`

const Divider = styled.div`
  width: 100%;
  padding: 0 20px;
  box-sizing: content-box;
  margin: 16px 0 16px -20px;
  background: rgba(115, 126, 141, 0.16);
  height: 1px;
`

const initialPnlSummary: PnlSummary = {
  won: {
    rounds: 0,
    amount: 0,
    payout: 0, // net payout after all deductions
    bestRound: {
      id: '0',
      payout: 0, // net payout after all deductions
      multiplier: 0,
    },
  },
  lost: {
    rounds: 0,
    amount: 0,
  },
  entered: {
    rounds: 0,
    amount: 0,
  },
}

export const getPnlSummary = (bets: Bet[], currentEpoch: number): PnlSummary => {
  return bets.reduce((summary: PnlSummary, bet) => {
    const roundResult = getRoundResult(bet, currentEpoch)
    if (roundResult === Result.WIN || roundResult === Result.CANCELED) {
      const payout = roundResult === Result.CANCELED ? 0 : getNetPayout(bet, REWARD_RATE)
      let { bestRound } = summary.won
      if (payout > bestRound.payout) {
        const { bullAmount, bearAmount, totalAmount } = bet.round
        const multiplier = getMultiplier(totalAmount, bet.position === BetPosition.BULL ? bullAmount : bearAmount)
        bestRound = { id: bet.round.epoch.toString(), payout, multiplier }
      }
      return {
        won: {
          rounds: summary.won.rounds + 1,
          amount: summary.won.amount + bet.amount,
          payout: summary.won.payout + payout,
          bestRound,
        },
        entered: {
          rounds: summary.entered.rounds + 1,
          amount: summary.entered.amount + bet.amount,
        },
        lost: summary.lost,
      }
    }
    if (roundResult === Result.LOSE || roundResult === Result.HOUSE) {
      return {
        lost: {
          rounds: summary.lost.rounds + 1,
          amount: summary.lost.amount + bet.amount,
        },
        entered: {
          rounds: summary.entered.rounds + 1,
          amount: summary.entered.amount + bet.amount,
        },
        won: summary.won,
      }
    }
    // Ignore Canceled and Live rounds
    return summary
  }, initialPnlSummary)
}

const PnlTab: React.FC<PnlTabProps> = ({ hasBetHistory, bets }) => {
  const { t } = useTranslation()
  const currentEpoch = useGetCurrentEpoch()
  const { payToken } = useConfig()
  const mjtPrice = usePriceMjtUSDT()
  const summary = getPnlSummary(bets, currentEpoch)
  const netResultAmount = summary.won.payout - summary.lost.amount
  const netResultIsPositive = netResultAmount > 0
  const avgPositionEntered = summary.entered.amount / summary.entered.rounds
  const avgBnbWonPerRound = netResultAmount / summary.entered.rounds
  const avgBnbWonIsPositive = avgBnbWonPerRound > 0

  // Guard in case user has only lost rounds
  const hasBestRound = summary.won.bestRound.payout !== 0

  const netResultInUsd = multiplyPriceByAmount(mjtPrice, netResultAmount)
  const avgBnbWonInUsd = multiplyPriceByAmount(mjtPrice, avgBnbWonPerRound)
  const avgBnbWonInUsdDisplay = !Number.isNaN(avgBnbWonInUsd) ? `~$${avgBnbWonInUsd.toFixed(2)}` : '~$0.00'
  const betRoundInUsd = multiplyPriceByAmount(mjtPrice, summary.won.bestRound.payout)
  const avgPositionEnteredInUsd = multiplyPriceByAmount(mjtPrice, avgPositionEntered)
  const avgPositionEnteredInUsdDisplay = !Number.isNaN(avgPositionEnteredInUsd)
    ? `~$${avgPositionEnteredInUsd.toFixed(2)}`
    : '~$0.00'

  return hasBetHistory ? (
    <Box p="0 24px 24px">
      <Text bold color="text" pb="8px">
        {t('PREDICTION_90')}
      </Text>
      <BorderContent>
        <Flex>
          <PnlChart lost={summary.lost.rounds} won={summary.won.rounds} />
          <Flex flexDirection="column" justifyContent="center" pl="17px">
            <Text fontSize="14px">{t('PREDICTION_25')}</Text>
            <Text
              bold
              fontSize="18px"
              lineHeight="1"
              color={netResultIsPositive ? 'primary' : '#F9479C'}
              mt="8px"
              mb="8px"
            >
              {`${netResultIsPositive ? '+' : ''}${formatMjt(netResultAmount)} ${payToken.symbol}`}
            </Text>
            <Text fontSize="14px" color="textRemark">
              {`~$${netResultInUsd.toFixed(2)}`}
            </Text>
          </Flex>
        </Flex>
      </BorderContent>
      <BorderContent margin="12px 0 0">
        <Text fontSize="14px" color="text" mb="6px">
          {t('PREDICTION_102')}
        </Text>
        <Text bold color={avgBnbWonIsPositive ? 'success' : '#F9479C'} mb="6px">
          {`${avgBnbWonIsPositive ? '+' : ''}${formatMjt(avgBnbWonPerRound)} ${payToken.symbol}`}
        </Text>
        <Text fontSize="14px" color="textRemark">
          {avgBnbWonInUsdDisplay}
        </Text>
      </BorderContent>
      {hasBestRound && (
        <BorderContent margin="12px 0 0">
          <Text bold color="text" mb="6px">
            {t('PREDICTION_103', { round: summary.won.bestRound.id })}
          </Text>
          <Flex alignItems="flex-end" mb="6px">
            <Text bold color="success">{`+${formatMjt(summary.won.bestRound.payout)} ${payToken.symbol}`}</Text>
            <Text ml="4px" small color="textRemark">
              ({summary.won.bestRound.multiplier.toFixed(2)}x)
            </Text>
          </Flex>
          <Text fontSize="14px" color="textRemark">
            {`~$${betRoundInUsd.toFixed(2)}`}
          </Text>
        </BorderContent>
      )}
      <BorderContent margin="12px 0 0">
        <Text fontSize="14px" color="text" mb="6px">
          {t('PREDICTION_104')}
        </Text>
        <Text bold mb="6px">{`${formatMjt(avgPositionEntered)} ${payToken.symbol}`}</Text>
        <Text fontSize="14px" color="textRemark">
          {avgPositionEnteredInUsdDisplay}
        </Text>
      </BorderContent>
      {/* <Divider /> */}
      <BorderContent margin="12px 0 0">
        <SummaryRow type="won" summary={summary} bnbBusdPrice={mjtPrice as any} />
        <Divider />
        <SummaryRow type="lost" summary={summary} bnbBusdPrice={mjtPrice as any} />
        <Divider />
        <SummaryRow type="entered" summary={summary} bnbBusdPrice={mjtPrice as any} />
      </BorderContent>
    </Box>
  ) : (
    <Box p="24px">
      <Flex justifyContent="center">
        <EmptyImg margin="20px auto 20px" />
      </Flex>
      <Text fontSize="14px" mb="8px" bold textAlign="center">
        {t('PREDICTION_76')}
      </Text>
      <Text as="p" textAlign="center" fontSize="14px" color="remark">
        {t('PREDICTION_77')}
      </Text>
    </Box>
  )
}

export default PnlTab
